import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { Alert, BlurWrapper, ConfidentialAlert, Loader } from 'ui';
import {
  ScoreWidget,
  ScoreWidgetMainColumn,
  ScoreWidgetSingleValueColumn,
  ScoreWidgetMultiValuesColumn,
} from 'ui/ScoreWidget';
import { getScoreWidgetFooterColor, formatDate } from 'shared/functions';
import { outlookTypes, outlookLabels } from 'shared/constants/outlookTypes';
import { creditRatingAlerts } from 'shared/constants/creditRating';
import { ProductionQuantityLimitsSharp } from '@mui/icons-material';

const useStyles = makeStyles(() => ({
  requestBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    height: '100%',
  },
}));

export const BlurredCompanyCreditRating = () => {
  const { t } = useTranslation();

  return (
    <ScoreWidget>
      <ScoreWidgetMainColumn label="Rating" value="-" footer="Outlook" footerColor="red" />
      <ScoreWidgetSingleValueColumn
        label={t('Latest rating action')}
        value="Rating action"
        subvalue="Date"
      />
      <ScoreWidgetSingleValueColumn
        label={t('Rating type')}
        value="Rating type"
        subvalue="Demand type"
      />
      <ScoreWidgetMultiValuesColumn
        label={t('Endorsement information')}
        values={[
          {
            label: t('Location'),
            value: t('Location'),
          },
          {
            label: t('Endorsement'),
            value: t('Endorsement'),
          },
        ]}
      />
      <ScoreWidgetMultiValuesColumn
        label={t('General information')}
        values={[
          {
            label: t('Lead analyst'),
            value: t('Lead analyst'),
          },
          {
            label: t('Committee chair'),
            value: t('Lead analyst'),
          },
        ]}
        last
      />
    </ScoreWidget>
  );
};

const CompanyCreditRating = ({
  companyName,
  loading,
  ratings,
  isPublicRating,
  isRatingSubscription,
  showConflictOfInterests,
  showRatingRequestInProgress,
  showRequestRatingBox,
  showRatings,
  onRequestClick,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <>
      {loading && <Loader center />}
      {showConflictOfInterests && (
        <Alert message={creditRatingAlerts.conflictOfInterests(companyName)} variant="warning" />
      )}
      {showRatings ? (
        <>
          {ratings.map(
            ({
              committeeChair,
              demandType,
              leadAnalyst,
              name,
              outlook,
              rating,
              preliminaryFlag,
              notchSuffix,
              ratingAction,
              ratingClassification,
              ratingDate,
              endorsementLocations,
              issuanceLocation,
            }) => (
              <Box mb={2} key={name}>
                <ScoreWidget>
                  <ScoreWidgetMainColumn
                    label={name}
                    value={rating}
                    suffix={notchSuffix}
                    preliminary={preliminaryFlag}
                    footer={`Outlook: ${t(outlookLabels[outlook])}`}
                    footerColor={getScoreWidgetFooterColor(outlook)}
                    accent={demandType === 'Unsolicited without participation'}
                  />
                  <ScoreWidgetSingleValueColumn
                    label={t('Latest rating action')}
                    value={ratingAction}
                    subvalue={formatDate(ratingDate)}
                  />
                  <ScoreWidgetSingleValueColumn
                    label={t('Rating type')}
                    value={ratingClassification}
                    subvalue={demandType}
                  />
                  <ScoreWidgetMultiValuesColumn
                    label={t('Endorsement information')}
                    values={[
                      {
                        label: t('Location'),
                        value: t(`${issuanceLocation || 'EU'} rated`),
                      },
                      {
                        label: t('Endorsement'),
                        value: t(endorsementLocations ? endorsementLocations.join(', ') : 'None'),
                      },
                    ]}
                  />
                  <ScoreWidgetMultiValuesColumn
                    label={t('General information')}
                    values={[
                      {
                        label: t('Lead analyst'),
                        value: leadAnalyst,
                      },
                      {
                        label: t('Committee chair'),
                        value: committeeChair,
                      },
                    ]}
                    last
                  />
                </ScoreWidget>
              </Box>
            ),
          )}
          {isRatingSubscription && (
            <ConfidentialAlert
              label={t(creditRatingAlerts.confidentialAlert.title)}
              message={t(creditRatingAlerts.confidentialAlert.message)}
            />
          )}
        </>
      ) : null}
      {!showRatings && (showRequestRatingBox || showRatingRequestInProgress) ? (
        <BlurWrapper
          content={
            <div className={classes.requestBox}>
              <Typography display="block">
                {!showRatingRequestInProgress
                  ? t('If you wish to see the rating for this company, please request it here.')
                  : t('Your rating request has been received.')}
              </Typography>
              <Typography display="block">
                {!showRatingRequestInProgress
                  ? t(
                      'If it is available and covered by your subscription, you will find it here immediately.',
                    )
                  : t(`Once available, information can be found here.`)}
              </Typography>
              <Box mt={1}>
                {!showRatingRequestInProgress ? (
                  <Button variant="contained" size="small" color="primary" onClick={onRequestClick}>
                    {t('Request')}
                  </Button>
                ) : (
                  <Button variant="contained" size="small" color="primary" disabled>
                    {t('Requested')}
                  </Button>
                )}
              </Box>
            </div>
          }
        >
          <BlurredCompanyCreditRating />
        </BlurWrapper>
      ) : null}
    </>
  );
};

CompanyCreditRating.defaultProps = {
  loading: false,
  ratings: [],
  isPublicRating: false,
  isRatingSubscription: false,
  showConflictOfInterests: false,
  showRatingRequestInProgress: false,
  showRequestRatingBox: false,
  showRatings: false,
};

CompanyCreditRating.propTypes = {
  companyName: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  ratings: PropTypes.arrayOf(
    PropTypes.shape({
      rating: PropTypes.string,
      outlook: PropTypes.oneOf([...Object.values(outlookTypes)]),
      name: PropTypes.string.isRequired,
      ratingDate: PropTypes.string.isRequired,
      leadAnalyst: PropTypes.string.isRequired,
      committeeChair: PropTypes.string.isRequired,
      demandType: PropTypes.string.isRequired,
      ratingAction: PropTypes.string.isRequired,
      ratingClassification: PropTypes.string.isRequired,
    }),
  ),
  isPublicRating: PropTypes.bool,
  isRatingSubscription: PropTypes.bool,
  showConflictOfInterests: PropTypes.bool,
  showRatingRequestInProgress: PropTypes.bool,
  showRequestRatingBox: PropTypes.bool,
  showRatings: PropTypes.bool,
  onRequestClick: PropTypes.func.isRequired,
};

export default CompanyCreditRating;
